import {
  Alert,
  Box,
  Card,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeftLong } from 'react-icons/fa6';
import Swal from 'sweetalert2';
import { LoadingButton } from '@mui/lab';
import { getAllContractTemplates, updateContractor } from '../../helper/backend_helper';
import InfoTooltip from '../InfoTooltip';

const emptyTerms = { percentage: '', contractDuration: '', terminationNotice: '', contractTemplate: '' };

const UpdateContractor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = location?.state?.userData;
  const [name, setName] = useState(userData?.name || '');
  const [standard, setStandard] = useState({
    percentage: userData?.standardPercentage ?? userData?.percentage ?? '',
    contractDuration: userData?.standardContractDuration || userData?.contractDuration || '',
    terminationNotice: userData?.standardTerminationNotice || userData?.terminationNotice || '',
    contractTemplate: userData?.standardContractTemplate?._id || userData?.contractTemplate?._id || userData?.standardContractTemplate || userData?.contractTemplate || '',
  });
  const [allInclusive, setAllInclusive] = useState({
    percentage: userData?.allInclusivePercentage ?? '',
    contractDuration: userData?.allInclusiveContractDuration || '',
    terminationNotice: userData?.allInclusiveTerminationNotice || '',
    contractTemplate: userData?.allInclusiveContractTemplate?._id || userData?.allInclusiveContractTemplate || '',
  });
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);

  async function fetchData() {
    try {
      const response = await getAllContractTemplates();
      setTemplates(response?.data?.data || []);
    } catch (error) {
      console.error('Error fetching contract templates:', error);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const standardTemplates = useMemo(() => templates.filter((template) => (template.mode || 'standard') === 'standard'), [templates]);
  const allInclusiveTemplates = useMemo(
    () => templates.filter((template) => template.mode === 'allInclusive'),
    [templates]
  );

  const updateTerms = (setter) => (field, value) => setter((prev) => ({ ...prev, [field]: value }));
  const setStandardField = updateTerms(setStandard);
  const setAllInclusiveField = updateTerms(setAllInclusive);

  const validate = () => {
    if (!name) return 'Name is required';
    if (!standard.contractTemplate) return 'Standard contract template is required';
    if (!standard.contractDuration) return 'Standard contract duration is required';
    if (standard.percentage === '') return 'Standard percentage is required';
    if (!standard.terminationNotice) return 'Standard cancellation notice is required';
    if (allInclusive.contractTemplate) {
      if (!allInclusive.contractDuration) return 'All Inclusive contract duration is required when its template is selected';
      if (allInclusive.percentage === '') return 'All Inclusive percentage is required when its template is selected';
      if (!allInclusive.terminationNotice) return 'All Inclusive cancellation notice is required when its template is selected';
    }
    return null;
  };

  const handleSave = async () => {
    try {
      const error = validate();
      if (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: error });
        return;
      }
      setLoading(true);
      const payload = {
        name,
        contractTemplate: standard.contractTemplate,
        contractDuration: standard.contractDuration,
        percentage: Number(standard.percentage),
        terminationNotice: standard.terminationNotice,
        standardContractTemplate: standard.contractTemplate,
        standardContractDuration: standard.contractDuration,
        standardPercentage: Number(standard.percentage),
        standardTerminationNotice: standard.terminationNotice,
        allInclusiveContractTemplate: allInclusive.contractTemplate || null,
        allInclusiveContractDuration: allInclusive.contractTemplate ? allInclusive.contractDuration : '',
        allInclusivePercentage: allInclusive.contractTemplate ? Number(allInclusive.percentage) : undefined,
        allInclusiveTerminationNotice: allInclusive.contractTemplate ? allInclusive.terminationNotice : '',
      };
      await updateContractor(userData?._id, payload, { headers: { 'Content-Type': 'application/json' } });
      Swal.fire({ icon: 'success', title: 'Contractor updated successfully' });
      
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: error?.response?.data?.errors?.[0]?.message || 'Failed to update contractor' });
    } finally {
      setLoading(false);
    }
  };

  const renderModeColumn = (title, description, values, setField, templateOptions, required) => (
    <Card variant="outlined" sx={{ p: 2, width: { xs: '100%', md: '48%' } }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>
      <Stack spacing={3}>
        <FormControl fullWidth required={required}>
          <FormLabel sx={{ mb: 1 }}>{title} Template{required ? ' *' : ''}</FormLabel>
          <Select value={values.contractTemplate} onChange={(e) => setField('contractTemplate', e.target.value)} displayEmpty>
            <MenuItem value=""><em>{required ? 'Select template' : 'Not offered to owner'}</em></MenuItem>
            {templateOptions.map((template) => <MenuItem key={template._id} value={template._id}>{template.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField fullWidth type="number" label="Percentage" value={values.percentage} onChange={(e) => setField('percentage', e.target.value)} required={required || !!values.contractTemplate} />
          <TextField fullWidth label="Duration" value={values.contractDuration} onChange={(e) => setField('contractDuration', e.target.value)} required={required || !!values.contractTemplate} />
        </Stack>
        <TextField fullWidth label="Cancellation Notice" value={values.terminationNotice} onChange={(e) => setField('terminationNotice', e.target.value)} required={required || !!values.contractTemplate} />
      </Stack>
    </Card>
  );

  return (
    <>
      <Helmet><title>Update Contractor</title></Helmet>
      <Card sx={{ padding: 3 }}>
        <Stack onClick={() => navigate(-1)} gap={3} sx={{ cursor: 'pointer', mb: 3 }} direction="row" alignItems="center">
          <FaArrowLeftLong size={24} />
          <Typography variant="h4">Update Contractor</Typography>
        </Stack>
        <Box component="form" noValidate autoComplete="off">
          <Stack sx={{ width: { xs: '100%', md: '48%' }, mb: 3 }}>
            <FormLabel sx={{ mb: 1 }}>Name <InfoTooltip title="Label for this contractor profile, e.g. the property owner or company the management contract is offered to." /></FormLabel>
            <TextField label="Enter Name" value={name} onChange={(e) => setName(e.target.value)} />
          </Stack>
          <Alert severity="info" sx={{ mb: 3 }}>The Standard template is mandatory. If you leave All Inclusive without a template, owners will only see the Standard option.</Alert>
          <Stack spacing={3} direction="row" useFlexGap flexWrap="wrap">
            {renderModeColumn('Standard', 'Default contract proposal shown to every owner.', standard, setStandardField, standardTemplates, true)}
            {renderModeColumn('All Inclusive', 'Optional alternative; only shown when a template is selected.', allInclusive, setAllInclusiveField, allInclusiveTemplates, false)}
          </Stack>
        </Box>
        <LoadingButton sx={{ width: 100, mt: 4, mb: 4, boxShadow: 'none', backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#1E3A5F', boxShadow: 'none' } }} variant="contained" onClick={handleSave} loading={loading}>Update</LoadingButton>
      </Card>
    </>
  );
};

export default UpdateContractor;
